import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil, Wallet } from 'lucide-react';

const CATEGORIES = [
  { key: 'pannelli', label: 'Fotovoltaico (kWp)' },
  { key: 'batteria', label: 'Batteria (kWh)' },
  { key: 'inverter', label: 'Inverter (kW)' },
  { key: 'wallbox', label: 'Wallbox (kW)' },
  { key: 'pompa_calore', label: 'Pompa di calore (kW)' },
  { key: 'climatizzatore', label: 'Climatizzatore (kW)' },
];

const emptyForm = { category: 'pannelli', label: '', power_min: '', power_max: '', amount_eur: '', notes: '', active: true };

export default function CompensationRates() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listCompensationRates();
      setRates(r || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      category: r.category, label: r.label,
      power_min: r.power_min ?? '', power_max: r.power_max ?? '',
      amount_eur: r.amount_eur, notes: r.notes || '', active: !!r.active,
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.category || !form.label || !form.amount_eur) { toast.error('Categoria, etichetta e importo obbligatori'); return; }
    setSaving(true);
    try {
      const body = {
        category: form.category,
        label: form.label,
        power_min: form.power_min !== '' ? Number(form.power_min) : null,
        power_max: form.power_max !== '' ? Number(form.power_max) : null,
        amount_eur: Number(form.amount_eur),
        notes: form.notes,
        active: form.active,
      };
      if (editingId) await api.updateCompensationRate(editingId, body);
      else await api.createCompensationRate(body);
      toast.success('Tariffa salvata');
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Eliminare la tariffa "${r.label}"?`)) return;
    try {
      await api.deleteCompensationRate(r.id);
      toast.success('Tariffa eliminata');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  return (
    <div>
      <PageHeader
        title="Tariffario Compensi Installatori"
        description="Definisci gli importi automatici che verranno assegnati agli installatori in base alla tipologia di prodotto e potenza. Applicati automaticamente quando assegni un intervento."
        actions={<Button onClick={openNew} data-testid="add-rate-btn"><Plus className="size-4 mr-2" />Nuova tariffa</Button>}
      />
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : rates.length === 0 ? (
            <div className="p-10 text-center">
              <Wallet className="size-8 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm">Nessuna tariffa configurata. Aggiungi la prima per abilitare il calcolo automatico dei compensi.</div>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Categoria</TableHead>
                <TableHead>Etichetta</TableHead>
                <TableHead>Range potenza</TableHead>
                <TableHead className="text-right">Compenso (€)</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rates.map(r => (
                  <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                    <TableCell><span className="text-xs uppercase">{r.category}</span></TableCell>
                    <TableCell className="font-medium">{r.label}</TableCell>
                    <TableCell className="text-xs tabular-nums">
                      {r.power_min ?? '—'} - {r.power_max ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">€ {Number(r.amount_eur).toFixed(2)}</TableCell>
                    <TableCell><span className={`text-xs ${r.active ? 'text-primary' : 'text-muted-foreground'}`}>{r.active ? 'Attiva' : 'Inattiva'}</span></TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-rate-${r.id}`}><Pencil className="size-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`delete-rate-${r.id}`}><Trash2 className="size-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica tariffa' : 'Nuova tariffa'}</DialogTitle>
            <DialogDescription>Es. "FV 3-6 kW" con range 3-6 e compenso €180 per kWp installato in questa fascia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="rate-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Etichetta (mostrata nel riepilogo)</Label>
              <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="es. FV 3-6 kW" data-testid="rate-label" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Potenza min</Label>
                <Input type="number" step="0.1" value={form.power_min} onChange={e => setForm(f => ({ ...f, power_min: e.target.value }))} placeholder="es. 3" data-testid="rate-min" />
              </div>
              <div>
                <Label className="text-xs">Potenza max</Label>
                <Input type="number" step="0.1" value={form.power_max} onChange={e => setForm(f => ({ ...f, power_max: e.target.value }))} placeholder="es. 6" data-testid="rate-max" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Compenso (€)</Label>
              <Input type="number" step="10" value={form.amount_eur} onChange={e => setForm(f => ({ ...f, amount_eur: e.target.value }))} placeholder="es. 180" data-testid="rate-amount" />
            </div>
            <div>
              <Label className="text-xs">Note (opzionale)</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} />
              <Label className="text-sm">Tariffa attiva</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="rate-save">{saving ? 'Salvo…' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
